import {
  openExternalUrl,
  reportDisallowedHost,
  EXTERNAL_HOST_ALLOWLIST,
  hasUnavailableMarker,
  responseFailMessage,
  safeArray,
  safeContent,
  safeObject,
  safeString
} from '@/views/layout/safeLayout'

const TYPE_ALIASES = {
  official: ['official', 'support', 'service', 'customer', 'telegram', 'tg'],
  telegram: ['telegram', 'tg', 'finance'],
  tg: ['telegram', 'tg', 'finance'],
  whatsapp: ['whatsapp', 'ws', 'finance'],
  ws: ['whatsapp', 'ws', 'finance']
}

const BLOCKED_MESSAGE = 'This service is temporarily unavailable. Please try again later.'
const LINK_BLOCKED_KEYS = [
  'technicalException',
  'technical_exception',
  'technicalMarker',
  'technical_marker',
  'technicalError',
  'technical_error',
  'backendFailure',
  'backend_failure',
  'backendBlocked',
  'backend_blocked',
  'failClosed',
  'fail_closed',
  'falseProof',
  'false_proof'
]

export function resolveCustomerServiceLink(response, type = 'official') {
  const target = normalizeType(type)
  const fallbackUrl = ''

  if (isBlockedResponse(response)) {
    return {
      ok: false,
      status: 'error',
      url: '',
      fallbackUrl,
      message: customerServiceFailMessage(response, BLOCKED_MESSAGE)
    }
  }

  if (!response) {
    return {
      ok: false,
      status: 'unavailable',
      url: '',
      fallbackUrl,
      message: BLOCKED_MESSAGE
    }
  }

  const content = safeContent(response)
  const direct = directUrl(content, target)
  if (direct) {
    return {
      ok: true,
      status: 'ok',
      url: direct,
      fallbackUrl,
      message: ''
    }
  }

  for (const key of ['url2', 'list', 'linkList', 'csLinkList']) {
    const url = findUrlInList(safeArray(content[key]), target)
    if (url) {
      return {
        ok: true,
        status: 'ok',
        url,
        fallbackUrl,
        message: ''
      }
    }
  }

  return {
    ok: false,
    status: 'unavailable',
    url: '',
    fallbackUrl,
    message: customerServiceFailMessage(response, BLOCKED_MESSAGE)
  }
}

export function resolveCustomerServiceUrl(response, type = 'official') {
  const link = resolveCustomerServiceLink(response, type)
  if (link.ok) return link.url
  return ''
}

export function openCustomerServiceUrl(response, type = 'official') {
  const link = resolveCustomerServiceLink(response, type)
  if (!link.ok) return false
  const url = link.url
  return openExternalUrl(url)
}

export function customerServiceFailMessage(response, fallback = BLOCKED_MESSAGE) {
  return responseFailMessage(response, fallback || BLOCKED_MESSAGE) || fallback || BLOCKED_MESSAGE
}

function isBlockedResponse(response) {
  if (!response) return false
  if (hasUnavailableMarker(response)) return true
  if (hasLinkBlocker(response)) return true
  return hasFailureStatus(response)
}

function hasLinkBlocker(response) {
  const body = safeObject(response)
  const content = safeContent(response)
  return [body, content].some(source => LINK_BLOCKED_KEYS.some(key => isActiveLinkBlocker(source[key])))
}

function isActiveLinkBlocker(value) {
  if (value === true || value === 1) return true
  if (value === false || value === 0) return false
  const token = safeString(value).trim().toLowerCase()
  if (!token || ['false', '0', 'no', 'off'].includes(token)) return false
  return ['true', '1', 'yes', 'on', 'blocked', 'unavailable', 'error', 'fail', 'failed'].includes(token) ||
    isFailureToken(token)
}

function normalizeType(type) {
  const value = safeString(type, 'official').toLowerCase()
  if (value === '1') return 'telegram'
  if (value === '2') return 'whatsapp'
  if (value.includes('whatsapp') || value === 'ws') return 'whatsapp'
  if (value.includes('telegram') || value === 'tg') return 'telegram'
  return 'official'
}

function hasFailureStatus(response) {
  const content = safeContent(response)
  return [
    response && response.status,
    response && response.state,
    response && response.result,
    response && response.code,
    response && response.errorCode,
    response && response.success,
    content.status,
    content.state,
    content.result,
    content.code,
    content.errorCode,
    content.success
  ].some(isFailureToken)
}

function isFailureToken(value) {
  const token = safeString(value).trim().toLowerCase()
  if (!token) return false
  return [
    'false',
    'fail',
    'failed',
    'failure',
    'error',
    'blocked',
    'unavailable',
    'readonly',
    'read_only',
    'not_supported',
    'unsupported',
    'unimplemented',
    'fail_closed',
    'failclosed',
    'false_proof',
    'falseproof',
    'technical_exception',
    'technicalexception',
    'backend_failure',
    'backendfailure'
  ].includes(token) || /^[45]\d\d$/.test(token) || token.startsWith('e_')
}

function directUrl(content, target) {
  const source = safeObject(content)
  const keys = target === 'whatsapp'
    ? ['ws', 'whatsapp', 'whatsappUrl', 'url']
    : target === 'telegram'
      ? ['tg', 'telegram', 'telegramUrl', 'url', 'link']
      : ['official', 'officialUrl', 'serviceUrl', 'customerServiceUrl', 'online', 'tg', 'telegram', 'url', 'link']
  for (const key of keys) {
    const value = safeHttpUrl(source[key])
    if (value) return value
  }
  return ''
}

function findUrlInList(list, target) {
  for (const raw of list) {
    const item = safeObject(raw)
    if (isBlockedResponse(item)) continue
    const explicit = urlByDirectChannel(item, target)
    if (explicit) return explicit
  }
  for (const raw of list) {
    const item = safeObject(raw)
    if (isBlockedResponse(item)) continue
    if (!matchesType(item, target)) continue
    const value = firstValue(item, ['link_url', 'linkUrl', 'url', 'href', 'value'])
    const url = safeHttpUrl(value)
    if (url) return url
  }
  return ''
}

function urlByDirectChannel(item, target) {
  if (target === 'whatsapp') return firstUrlValue(item, ['ws', 'whatsapp'])
  if (target === 'telegram') return firstUrlValue(item, ['tg', 'telegram'])
  return firstUrlValue(item, ['official', 'online', 'tg', 'telegram', 'link_url', 'linkUrl', 'url', 'href', 'value'])
}

function matchesType(item, target) {
  const haystack = firstValue(item, ['link_type', 'linkType', 'type', 'channel', 'name', 'title']).toLowerCase()
  if (!haystack) return target === 'official'
  return TYPE_ALIASES[target].some(alias => haystack.includes(alias))
}

function firstValue(source, keys) {
  for (const key of keys) {
    const value = safeString(source[key]).trim()
    if (value) return value
  }
  return ''
}

function firstUrlValue(source, keys) {
  for (const key of keys) {
    const value = safeHttpUrl(source[key])
    if (value) return value
  }
  return ''
}

function safeHttpUrl(value) {
  const url = safeString(value).trim()
  try {
    const parsed = new URL(url)
    if (!['http:', 'https:'].includes(parsed.protocol)) return ''
    if (!parsed.hostname) return ''
    reportDisallowedHost('customerService', url, EXTERNAL_HOST_ALLOWLIST) // log-only:仅观测,不拦截
    return url
  } catch (e) {
    return ''
  }
}
